/**
 * History AMD display + Outcome enum. Mapping only — does not classify audio.
 *
 * Outcome is derived from hangup_cause + stored amd_result + far_end_class
 * (+ call_type as phase overlay). AMD is never invented: empty stays empty.
 *
 * Gold (UI truth vs Dial event.log):
 * - Connecting TTS = SCREENING (not Human)
 * - After quiet, LEFT speech (call_type=human_live) = HUMAN
 * - Empty RX ≠ human (DEAD_AIR / SCREENING)
 * - MACHINE = AVMD beep only (beepless ≠ VM)
 */

export const HISTORY_OUTCOMES = [
  "answered-human",
  "screening",
  "vm",
  "no-connect",
  "operator-hangup",
  "error",
] as const;

export type HistoryOutcome = (typeof HISTORY_OUTCOMES)[number];

export const HISTORY_AMD = ["HUMAN", "MACHINE", "SCREENING", "UNKNOWN"] as const;

export type HistoryAmd = (typeof HISTORY_AMD)[number];

/** Dial event.log copy. History AMD dropdowns/pills must match these. */
export const AMD_OPERATOR_LABEL: Record<HistoryAmd, string> = {
  HUMAN: "👤 Human",
  MACHINE: "🤖 VM",
  SCREENING: "📱 Screening",
  UNKNOWN: "❓ Unknown",
};

const BEEP_AMD = new Set(["MACHINE", "VM", "VOICEMAIL", "BEEP", "BEEP_VM"]);
const BEEPLESS_AMD = new Set(["BEEPLESS_VM"]);
/**
 * Engine labels (@amd wave-2) that arrive in call_type / amd_result.
 * These are STORED engine classifications, not invented audio guesses:
 * - pstn_ring: ring-cadence guard — never a person → 📱 Screening family
 * - recording_tone: E.180 1400 Hz warning tone (never MACHINE / never vm)
 * - carrier_intercept: SIT triad → ❓ Unknown (never HUMAN)
 * Mirrors `_ENGINE_LABEL_PHASES` in apps/web history.py.
 */
const ENGINE_LABEL_PHASE: Record<string, HistoryAmd> = {
  pstn_ring: "SCREENING",
  recording_tone: "SCREENING",
  carrier_intercept: "UNKNOWN",
};
const ENGINE_LABEL_AMD: Record<string, HistoryAmd> = {
  PSTN_RING: "SCREENING",
  RECORDING_TONE: "SCREENING",
  CARRIER_INTERCEPT: "UNKNOWN",
};
const SCREEN_FAR = new Set(["IVR_MENU", "AI_VOICE_AGENT", "FRAUD_OR_SECURITY_SCREENER"]);
const SCREEN_PHASE = new Set([
  "screening_tts",
  "screening_talk",
  "please_wait",
  "connecting",
  "screening_pause",
  "mid_screen_hello",
]);
const HUMAN_PHASE = new Set(["human_live", "hello", "short_hello"]);
const BEEPLESS_PHASE = new Set(["beepless_vm"]);
const DEAD_AIR_FAR = new Set(["DEAD_AIR", "DEAD_AIR_FALSE_ANSWER"]);
const DEAD_AIR_PHASE = new Set([
  "silence",
  "apple_gap",
  "apple_gap_quiet_ident",
  "apple_gap_quiet_wait",
  "empty_rx",
]);
const OPERATOR_CAUSE = new Set(["OPERATOR_HANGUP", "OPERATOR_TIMEOUT"]);
const VM_CAUSE = new Set(["MACHINE"]);
const SCREEN_CAUSE = new Set(["SCREENING_NO_CONNECT"]);
const NO_CONNECT_CAUSE = new Set([
  "NO_ANSWER",
  "NO_USER_RESPONSE",
  "USER_BUSY",
  "SUBSCRIBER_ABSENT",
  "ORIGINATOR_CANCEL",
  "ALLOTTED_TIMEOUT",
  "CALL_REJECTED",
  "NO_ROUTE_DESTINATION",
  "UNALLOCATED_NUMBER",
]);
const ERROR_CAUSE = new Set([
  "ERROR",
  "NORMAL_TEMPORARY_FAILURE",
  "DESTINATION_OUT_OF_ORDER",
  "NETWORK_OUT_OF_ORDER",
  "GATEWAY_DOWN",
  "INVALID_GATEWAY",
  "INTERWORKING",
  "RECOVERY_ON_TIMER_EXPIRE",
  "PROTOCOL_ERROR",
  "SWITCH_CONGESTION",
  "NORMAL_CIRCUIT_CONGESTION",
  "REQUESTED_CHAN_UNAVAIL",
  "INCOMPATIBLE_DESTINATION",
  "BEARERCAPABILITY_NOTAVAIL",
  "MANDATORY_IE_MISSING",
  "WRONG_MESSAGE",
]);

export type HistoryAmdRow = {
  hangup_cause?: string | null;
  amd_result?: string | null;
  call_type?: string | null;
  far_end_class?: string | null;
  final_match?: boolean | null;
  digits_attempts?: string[] | null;
  operator_decisions?: Array<{ action?: string | null; attempt?: number | null; digits?: string | null }> | null;
  duration_ms?: number | null;
  judgment_outcome?: string | null;
  /** Raw stored CDR judgment (expected-code match). Never the Outcome. */
  judgment_raw?: string | null;
  otp_state?: string | null;
  /** Client-entered OTP/DTMF. Raw (operator policy); `len:N` only for legacy tokens. */
  code?: string | null;
  digits?: string | null;
  /** Expected OTP for the operator to confirm against. Raw; `null` when absent/legacy token. */
  expected_code?: string | null;
  /** Recording basename (never a directory path). */
  recording_path?: string | null;
  /** On-disk recording size in bytes, when the API could stat the file. */
  recording_bytes?: number | null;
};

const DTMF_RE = /^[0-9A-D#*]{1,24}$/;

function upper(value: unknown): string {
  return String(value ?? "").trim().toUpperCase();
}

/** True when a history digit field is a warehouse len/sha token, not the code. */
export function isShaDigitToken(value: unknown): boolean {
  if (value && typeof value === "object" && value !== null && "sha256" in value) return true;
  const text = String(value ?? "").trim();
  if (!text) return false;
  const lowered = text.toLowerCase();
  if (lowered.includes("sha256")) return true;
  if (text.startsWith("{") && lowered.includes("len")) return true;
  return false;
}

/** Entered DTMF only. Hashes, JSON, and length labels return empty. */
export function historyPlainDigits(value: unknown): string {
  if (value == null || value === "") return "";
  if (isShaDigitToken(value)) return "";
  if (typeof value === "object") {
    const rec = value as Record<string, unknown>;
    for (const key of ["digits", "code", "entered", "otp"] as const) {
      const inner = historyPlainDigits(rec[key]);
      if (inner) return inner;
    }
    return "";
  }
  const text = String(value).trim().replace(/#$/, "");
  return DTMF_RE.test(text) ? text : "";
}

const LEN_LABEL_RE = /^len:(\d+)$/i;

/** `len:N` for a stored code. Never the OTP itself. */
export function historyCodeLabel(value: unknown): string {
  if (value == null || value === "") return "";
  if (typeof value === "object") {
    const rec = value as Record<string, unknown>;
    const n = Number(rec.len);
    if (Number.isFinite(n) && n > 0) return `len:${Math.trunc(n)}`;
    for (const key of ["digits", "code", "entered", "otp"] as const) {
      const inner = historyCodeLabel(rec[key]);
      if (inner) return inner;
    }
    return "";
  }
  const text = String(value).trim();
  const labeled = LEN_LABEL_RE.exec(text);
  if (labeled) return `len:${labeled[1]}`;
  const token = /"len"\s*:\s*(\d+)|len:(\d+)/i.exec(text);
  if (isShaDigitToken(value) && token) {
    const n = token[1] || token[2];
    return n && Number(n) > 0 ? `len:${n}` : "";
  }
  const plain = historyPlainDigits(value);
  return plain ? `len:${plain.length}` : "";
}

/**
 * History code cell. Raw entered OTP (operator confirm policy: expected vs
 * entered is visible). `len:N` only as a fallback when the store kept a
 * legacy `{len,sha256}` warehouse token — the digits were never persisted then.
 */
export function historyEnteredCode(row: HistoryAmdRow): string {
  // Raw first: dedicated key, then last attempt / last decision (matches server).
  for (const candidate of [row.code, row.digits]) {
    const plain = historyPlainDigits(candidate);
    if (plain) return plain;
  }
  let last = "";
  for (const item of row.digits_attempts ?? []) {
    const plain = historyPlainDigits(item);
    if (plain) last = plain;
  }
  if (last) return last;
  for (const decision of row.operator_decisions ?? []) {
    const plain = historyPlainDigits(decision?.digits);
    if (plain) last = plain;
  }
  if (last) return last;
  // Legacy token fallback: length label only.
  for (const candidate of [row.code, row.digits]) {
    const label = historyCodeLabel(candidate);
    if (label) return label;
  }
  last = "";
  for (const item of row.digits_attempts ?? []) {
    const label = historyCodeLabel(item);
    if (label) last = label;
  }
  if (last) return last;
  for (const decision of row.operator_decisions ?? []) {
    const label = historyCodeLabel(decision?.digits);
    if (label) last = label;
  }
  return last;
}

/** Short decision line: Accept · #1. Never dumps digit JSON. */
export function historyDecisionLabel(decision: {
  action?: string | null;
  attempt?: number | null;
  digits?: string | null;
}): string {
  const action = String(decision.action ?? "").trim();
  if (!action) return "";
  const attempt = decision.attempt != null && Number.isFinite(Number(decision.attempt)) ? ` · #${decision.attempt}` : "";
  return `${action}${attempt}`;
}

function lower(value: unknown): string {
  return String(value ?? "").trim().toLowerCase();
}

/**
 * Canonical AMD from the stored field only. Empty if blank.
 * Beepless is SCREENING (not Machine). Does not invent HUMAN.
 */
export function historyAmd(value: unknown): HistoryAmd | "" {
  const key = upper(value);
  if (!key) return "";
  if (key in ENGINE_LABEL_AMD) return ENGINE_LABEL_AMD[key];
  if (BEEPLESS_AMD.has(key)) return "SCREENING";
  if (BEEP_AMD.has(key)) return "MACHINE";
  if (key === "HUMAN" || key === "SCREENING" || key === "UNKNOWN") return key;
  if (key === "👤 HUMAN" || key.endsWith(" HUMAN")) return "HUMAN";
  if (key === "🤖 VM" || key.endsWith(" VM")) return "MACHINE";
  if (key === "📱 SCREENING" || key.endsWith(" SCREENING")) return "SCREENING";
  if (key === "❓ UNKNOWN" || key.endsWith(" UNKNOWN")) return "UNKNOWN";
  return "";
}

/** Table/CSV stored text: canonical enum, else the stored string, else empty. */
export function historyAmdDisplay(value: unknown): string {
  const canon = historyAmd(value);
  if (canon) return canon;
  return String(value ?? "").trim();
}

/**
 * AMD column / filter / CSV. Canonical stored amd_result only.
 * Does not overlay far_end_class or call_type (those belong on Outcome).
 * Empty stays empty except engine labels that landed in call_type.
 */
export function historyAmdFromRow(row: HistoryAmdRow): HistoryAmd | "" {
  const storedRaw = upper(row.amd_result);
  const stored = historyAmd(row.amd_result);
  if (stored) return stored;
  if (storedRaw) return "";
  const phase = lower(row.call_type);
  return ENGINE_LABEL_PHASE[phase] ?? "";
}

/** Dial-matching AMD pill/dropdown label. Empty when AMD was never stored. */
export function historyAmdOperatorLabel(row: HistoryAmdRow): string {
  const amd = historyAmdFromRow(row);
  if (amd) return AMD_OPERATOR_LABEL[amd];
  return historyAmdDisplay(row.amd_result);
}

/** Live header AMD. Canonical amd_label only — Class must not rewrite it. */
export function consoleAmdFromSnapshot(snap: {
  amd_label?: string | null;
  far_end_class?: string | null;
  party?: { amd?: string | null; hangup_cause?: string | null };
}): HistoryAmd | "" {
  return historyAmd(snap.amd_label || snap.party?.amd);
}

const AMD_LINE_BASES: Array<{ amd: HistoryAmd; base: string }> = [
  { amd: "HUMAN", base: AMD_OPERATOR_LABEL.HUMAN },
  { amd: "MACHINE", base: AMD_OPERATOR_LABEL.MACHINE },
  { amd: "SCREENING", base: AMD_OPERATOR_LABEL.SCREENING },
  { amd: "UNKNOWN", base: AMD_OPERATOR_LABEL.UNKNOWN },
];

/** Canonical AMD from an engine operator line. Empty when the line is not AMD. */
export function amdLabelFromOperatorLine(line: string): HistoryAmd | "" {
  const trimmed = line.trim();
  const hit = AMD_LINE_BASES.find(
    ({ base }) => trimmed === base || trimmed.startsWith(`${base} ·`) || trimmed.startsWith(`${base} `),
  );
  return hit?.amd ?? "";
}

/**
 * Do not call this on inbound `log.operator` / `session.ended` lines.
 * A stale SCREENING snapshot rewrites Human/VM and dedupe then drops them.
 */
export function rewriteOperatorAmdLine(line: string, amd: HistoryAmd | ""): string {
  if (!amd) return line;
  const trimmed = line.trim();
  const hit = AMD_LINE_BASES.find(
    ({ base }) => trimmed === base || trimmed.startsWith(`${base} ·`) || trimmed.startsWith(`${base} `),
  );
  if (!hit) return line;
  const target = AMD_OPERATOR_LABEL[amd];
  if (trimmed === hit.base) return target;
  if (amd === "HUMAN") return `${target}${trimmed.slice(hit.base.length)}`;
  return target;
}

function answeredWork(row: HistoryAmdRow): boolean {
  if (row.final_match === true) return true;
  if ((row.digits_attempts ?? []).some((item) => String(item ?? "").trim())) return true;
  return (row.operator_decisions ?? []).some((item) => {
    const action = String(item?.action ?? "").trim().toLowerCase();
    return action === "accept" || action === "retry" || action === "resend";
  });
}

export function historyOutcome(row: HistoryAmdRow): HistoryOutcome {
  const cause = upper(row.hangup_cause);
  const amd = historyAmdFromRow(row);
  const far = upper(row.far_end_class);
  const phase = lower(row.call_type);
  const live = HUMAN_PHASE.has(phase);
  const dead = DEAD_AIR_FAR.has(far) || DEAD_AIR_PHASE.has(phase);
  const screenish =
    SCREEN_PHASE.has(phase) || BEEPLESS_PHASE.has(phase) || SCREEN_FAR.has(far) || dead;

  if (OPERATOR_CAUSE.has(cause)) return "operator-hangup";
  if (ERROR_CAUSE.has(cause)) return "error";
  if (NO_CONNECT_CAUSE.has(cause)) return "no-connect";
  if (SCREEN_CAUSE.has(cause)) return "screening";

  // Confirmed pickup wins over a stale MACHINE label. Screen / beepless /
  // dead-air / mid_screen_hello must not become Human even if amd_result says so.
  if (live) return "answered-human";
  if (screenish) return "screening";
  if (amd === "HUMAN") return "answered-human";
  if (amd === "MACHINE") return "vm";
  if (VM_CAUSE.has(cause) && amd !== "SCREENING") return "vm";
  if (amd === "SCREENING") return "screening";

  if (cause === "NORMAL_CLEARING" || cause === "SUCCESS" || !cause) {
    if (answeredWork(row)) return "answered-human";
    return "no-connect";
  }
  return "error";
}

export const HISTORY_OUTCOME_LABEL: Record<HistoryOutcome, string> = {
  "answered-human": "answered-human",
  screening: "screening",
  vm: "vm",
  "no-connect": "no-connect",
  "operator-hangup": "operator-hangup",
  error: "error",
};

export function isHistoryOutcome(value: string | null | undefined): value is HistoryOutcome {
  return (HISTORY_OUTCOMES as readonly string[]).includes((value ?? "").trim());
}

function decisionAction(item: { action?: string | null } | string | null | undefined): string {
  if (item == null) return "";
  if (typeof item === "string") return item.trim().toLowerCase();
  return String(item.action ?? "").trim().toLowerCase();
}

/** Operator Accept is authoritative over expected_code mismatch. */
export function operatorAccepted(row: HistoryAmdRow): boolean {
  return (row.operator_decisions ?? []).some((item) => decisionAction(item) === "accept");
}

/**
 * Match column. Accept or final_match true = yes. Do not invert a successful OTP.
 */
export function historyMatch(row: HistoryAmdRow): boolean | null {
  if (row.final_match === true || operatorAccepted(row)) return true;
  if (row.final_match === false) return false;
  return null;
}

/**
 * Judge column. CPA OTP_MISMATCH must not override operator Accept / matched OTP.
 */
export function historyJudge(row: HistoryAmdRow): string {
  const stored = String(row.judgment_outcome ?? "").trim();
  const key = stored.toLowerCase();
  if (operatorAccepted(row) || row.final_match === true) {
    if (!key || key === "fail" || key === "void" || key === "unknown" || key === "partial") {
      return "success";
    }
    return stored;
  }
  return stored;
}

/** OTP state for History expand. Accept / match → verified. */
export function historyOtpState(row: HistoryAmdRow): string {
  const stored = String(row.otp_state ?? "").trim();
  if (operatorAccepted(row) || row.final_match === true) {
    const key = stored.toLowerCase();
    if (!key || key === "mismatch" || key === "collected" || key === "unknown" || key === "prompted" || key === "issued") {
      return "verified";
    }
  }
  return stored;
}

export function outcomeTone(value: string | null | undefined): "ok" | "live" | "fail" | "mute" {
  switch ((value ?? "").trim()) {
    case "answered-human":
      return "ok";
    case "screening":
      return "live";
    case "vm":
    case "error":
      return "fail";
    case "operator-hangup":
      return "live";
    default:
      return "mute";
  }
}

/* ------------------------------------------------------------------ */
/* Hangup-cause clarity: engine vs network vs operator (wave-2).       */
/* ------------------------------------------------------------------ */

export type HangupClass = "engine" | "network" | "operator" | "other";

const ENGINE_HANGUP = new Set(["SCREENING_NO_CONNECT", "MACHINE"]);
const OPERATOR_HANGUP_CAUSES = new Set(["OPERATOR_HANGUP", "OPERATOR_TIMEOUT"]);
/** Remote/network-decided causes. NORMAL_CLEARING is the remote end hanging up. */
const NETWORK_HANGUP = new Set([
  "NORMAL_CLEARING",
  "INTERWORKING",
  "NO_ANSWER",
  "NO_USER_RESPONSE",
  "USER_BUSY",
  "SUBSCRIBER_ABSENT",
  "ORIGINATOR_CANCEL",
  "ALLOTTED_TIMEOUT",
  "CALL_REJECTED",
  "NO_ROUTE_DESTINATION",
  "UNALLOCATED_NUMBER",
  "NORMAL_TEMPORARY_FAILURE",
  "DESTINATION_OUT_OF_ORDER",
  "NETWORK_OUT_OF_ORDER",
  "GATEWAY_DOWN",
  "INVALID_GATEWAY",
  "RECOVERY_ON_TIMER_EXPIRE",
  "PROTOCOL_ERROR",
  "SWITCH_CONGESTION",
  "NORMAL_CIRCUIT_CONGESTION",
  "REQUESTED_CHAN_UNAVAIL",
  "INCOMPATIBLE_DESTINATION",
  "BEARERCAPABILITY_NOTAVAIL",
  "MANDATORY_IE_MISSING",
  "WRONG_MESSAGE",
]);

/** Who ended the call: engine decision, network/remote, or the operator. */
export function hangupCauseClass(cause: string | null | undefined): HangupClass {
  const key = upper(cause);
  if (!key) return "other";
  if (OPERATOR_HANGUP_CAUSES.has(key)) return "operator";
  if (ENGINE_HANGUP.has(key)) return "engine";
  if (NETWORK_HANGUP.has(key)) return "network";
  return "other";
}

export const HANGUP_CLASS_LABEL: Record<HangupClass, string> = {
  engine: "engine",
  network: "network",
  operator: "operator",
  other: "cause",
};

/** Pill tone per hangup class. Operator = teal, engine = amber, network = mute. */
export function hangupClassTone(cls: HangupClass): "teal" | "live" | "mute" {
  if (cls === "operator") return "teal";
  if (cls === "engine") return "live";
  return "mute";
}

/* ------------------------------------------------------------------ */
/* Judgment truthfulness: code-match judgment ≠ call Outcome (FC8).    */
/* ------------------------------------------------------------------ */

/**
 * Raw stored CDR judgment (expected-code match). Prefers `judgment_raw`;
 * falls back to `judgment_outcome` only when the API did not split them.
 * This is a code-match signal — never render it as the call's Outcome.
 */
export function judgmentCodeMatch(row: HistoryAmdRow): string {
  // Operator Accept is the verdict. Stored CDR fail (no expected_code)
  // must not keep the chip on code-match: fail (live 7262211f).
  if (operatorAccepted(row) || row.final_match === true) {
    return historyJudge(row);
  }
  const raw = String(row.judgment_raw ?? "").trim();
  if (raw) return raw;
  return String(row.judgment_outcome ?? "").trim();
}

/**
 * Neutral chip label: `code-match: fail`. Empty when no judgment stored.
 */
export function judgmentChipLabel(row: HistoryAmdRow): string {
  const value = judgmentCodeMatch(row);
  return value ? `code-match: ${value.toLowerCase()}` : "";
}

/**
 * Judgment chip tone. NEVER "fail": a fail judgment on a successful
 * operator-Accepted call is a code-match mismatch, not a failed call.
 * success → ok, anything else → mute (neutral).
 */
export function judgmentChipTone(row: HistoryAmdRow): "ok" | "mute" {
  return judgmentCodeMatch(row).toLowerCase() === "success" ? "ok" : "mute";
}

/* ------------------------------------------------------------------ */
/* Recording evidence flags (FC1): missing / truncated audio.          */
/* ------------------------------------------------------------------ */

export type RecordingEvidence = "ok" | "missing" | "truncated" | "unknown";

/** Stereo 8 kHz 16-bit PCM ≈ 32,000 bytes/s + 44-byte WAV header. */
const WAV_BYTES_PER_SEC = 32_000;
const WAV_HEADER_BYTES = 44;
/** Below this fraction of the expected size the recording counts as truncated. */
const TRUNCATED_RATIO = 0.5;

/**
 * Evidence flag for the recording attached to a row.
 * - `missing`: no recording path, or the file could not be found (0 bytes).
 * - `truncated`: on-disk size far below what duration_ms implies (FC1:
 *   57,004 B ≈ 1.78 s of audio on a 64.6 s call).
 * - `unknown`: API did not report a byte size and a path exists.
 * - `ok`: path present and size consistent with the call duration.
 * Never invents audio: an absent path stays `missing`.
 */
export function recordingEvidence(row: HistoryAmdRow): RecordingEvidence {
  const name = String(row.recording_path ?? "").trim();
  const bytes =
    row.recording_bytes === undefined
      ? undefined
      : typeof row.recording_bytes === "number"
        ? row.recording_bytes
        : null;
  const durationMs = typeof row.duration_ms === "number" ? row.duration_ms : null;
  if (!name) return "missing";
  // undefined: API predates the evidence field — cannot tell, keep legacy link.
  if (bytes === undefined) return "unknown";
  // null / 0 / header-only: backend could not find a playable file.
  if (bytes === null || bytes <= WAV_HEADER_BYTES) return "missing";
  if (durationMs !== null && durationMs > 0) {
    const expected = (durationMs / 1000) * WAV_BYTES_PER_SEC + WAV_HEADER_BYTES;
    if (expected > 0 && bytes < expected * TRUNCATED_RATIO) return "truncated";
  }
  return "ok";
}

/** Tooltip copy for the evidence badge. Empty string when evidence is ok. */
export function recordingEvidenceTooltip(row: HistoryAmdRow): string {
  const evidence = recordingEvidence(row);
  const bytes = typeof row.recording_bytes === "number" ? row.recording_bytes : null;
  const seconds =
    typeof row.duration_ms === "number" && row.duration_ms > 0
      ? (row.duration_ms / 1000).toFixed(1)
      : null;
  if (evidence === "missing") {
    return "No playable recording stored for this call — audio evidence is missing.";
  }
  if (evidence === "truncated") {
    const have = bytes !== null ? `${Math.round(bytes / 1024)} KB on disk` : "recording shorter than";
    const want = seconds ? `a ${seconds}s call` : "the call duration";
    return `Recording looks truncated: ${have} for ${want}. Audio evidence may stop early.`;
  }
  return "";
}

/** Recording is linkable/playable only when evidence is not missing. */
export function recordingPlayable(row: HistoryAmdRow): boolean {
  const evidence = recordingEvidence(row);
  return evidence === "ok" || evidence === "truncated" || evidence === "unknown";
}
